/**
 * ArtistIndex — «Исполнители» by letters, with the strip A–Z at the right edge. Task 69.
 *
 * `docs/superpowers/specs/2026-10-25-oblik-afisha-design.md` «Экраны → «Треки»»: a large gold
 * letter, the names under it, and a strip of the letters the list has — Latin, then Cyrillic,
 * then «#» (`lib/letters.ts`). A tap on a letter, or a finger run along the strip, makes that
 * letter the first row under the header.
 *
 * ## Two kinds of rows, both declared
 *
 * The virtual list lays rows out from declared heights (`components/VirtualList.tsx`), so a
 * letter is {@link LETTER_HEIGHT} and an artist {@link ARTIST_HEIGHT}, each equal to its token
 * (`--letter-h`, `--artist-h`) and held there by a test. A letter's place is the sum of the rows
 * above it, which is how the strip knows where to scroll without measuring a row.
 *
 * ## The strip is one target
 *
 * Up to 57 letters in the height of a phone cannot each be 44 points tall. The finger target is
 * the whole strip, `--hit` wide, and the letter is the one under the finger — as in «Контакты».
 * Every letter is also a button of its own, for a screen reader and a keyboard. The strip hangs
 * from a sticky line under the tab's header, as tall as the scroller shows below it.
 */

import type { JSX, PointerEvent as ReactPointerEvent } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ScrollPortContext } from '../app/scroll-port';
import type { ArtistItem } from '../lib/api';
import { trackCount } from '../lib/format';
import { groupByLetter } from '../lib/letters';
import type { Collection } from '../store/library';

import { VirtualList, rowOffsets } from './VirtualList';

/** Pixels. Equal to `--letter-h`. */
export const LETTER_HEIGHT = 36;
/** Pixels. Equal to `--artist-h`. */
export const ARTIST_HEIGHT = 54;

/** The accessible name of a letter of the strip. */
export function letterLabel(letter: string): string {
  return `Буква ${letter}`;
}

type IndexRow =
  | {
      readonly key: string;
      readonly height: number;
      readonly kind: 'letter';
      readonly letter: string;
    }
  | {
      readonly key: string;
      readonly height: number;
      readonly kind: 'artist';
      readonly artist: ArtistItem;
    };

function indexRows(artists: readonly ArtistItem[]): IndexRow[] {
  return groupByLetter(artists, (artist) => artist.key).flatMap((group) => [
    {
      key: `l:${group.letter}`,
      height: LETTER_HEIGHT,
      kind: 'letter' as const,
      letter: group.letter,
    },
    ...group.items.map((artist) => ({
      key: `a:${artist.key}`,
      height: ARTIST_HEIGHT,
      kind: 'artist' as const,
      artist,
    })),
  ]);
}

interface Geometry {
  /** The sticky header's height: a row is first under it, not under the top edge. */
  readonly headH: number;
  /** The list's top in the scroller's content coordinates. */
  readonly listTop: number;
  /** How tall the scroller shows. */
  readonly viewH: number;
}

export function ArtistIndex({
  artists,
  label,
  onOpen,
}: {
  readonly artists: readonly ArtistItem[];
  readonly label: string;
  readonly onOpen: (collection: Collection) => void;
}): JSX.Element {
  const port = useContext(ScrollPortContext);
  const wrap = useRef<HTMLDivElement | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);
  /** The finger running along the strip, while it is there. */
  const dragging = useRef<number | undefined>(undefined);

  const rows = useMemo(() => indexRows(artists), [artists]);
  const offsets = useMemo(() => rowOffsets(rows), [rows]);
  const letters = useMemo(
    () =>
      rows.flatMap((row, index) => (row.kind === 'letter' ? [{ letter: row.letter, index }] : [])),
    [rows],
  );

  const [current, setCurrent] = useState<string | undefined>(undefined);
  const [box, setBox] = useState<{ readonly top: number; readonly height: number }>({
    top: 0,
    height: 0,
  });

  const measure = useCallback((): Geometry | undefined => {
    // The virtual list's own box: it is the list's top, wherever the scroller has gone.
    const list = wrap.current?.querySelector<HTMLElement>('[data-virtual]');
    if (port === null || list === null || list === undefined) return undefined;
    const head = port.querySelector<HTMLElement>('.tracksHead');
    const headH = head?.getBoundingClientRect().height ?? 0;
    const listTop =
      list.getBoundingClientRect().top - port.getBoundingClientRect().top + port.scrollTop;
    return { headH, listTop, viewH: port.clientHeight };
  }, [port]);

  /** The letter of the first row under the header. */
  const follow = useCallback((): void => {
    const geometry = measure();
    if (geometry === undefined || port === null) return;
    const y = port.scrollTop - geometry.listTop + geometry.headH;
    let found = letters[0]?.letter;
    for (const entry of letters) {
      if ((offsets[entry.index] ?? 0) <= y + 1) found = entry.letter;
      else break;
    }
    setCurrent(found);
  }, [measure, port, letters, offsets]);

  // The strip is as tall as the scroller shows under the header, and moves with neither.
  useLayoutEffect(() => {
    const place = (): void => {
      const geometry = measure();
      if (geometry === undefined) return;
      setBox({ top: geometry.headH, height: Math.max(0, geometry.viewH - geometry.headH) });
    };
    place();
    window.addEventListener('resize', place);
    const observer =
      typeof ResizeObserver === 'function' && port !== null ? new ResizeObserver(place) : undefined;
    if (port !== null) observer?.observe(port);
    return () => {
      window.removeEventListener('resize', place);
      observer?.disconnect();
    };
  }, [measure, port]);

  useEffect(() => {
    follow();
    if (port === null) return undefined;
    port.addEventListener('scroll', follow, { passive: true });
    return () => {
      port.removeEventListener('scroll', follow);
    };
  }, [follow, port]);

  const jump = useCallback(
    (at: number): void => {
      const entry = letters[at];
      const geometry = measure();
      if (entry === undefined || geometry === undefined || port === null) return;
      port.scrollTop = Math.max(0, geometry.listTop + (offsets[entry.index] ?? 0) - geometry.headH);
      setCurrent(entry.letter);
    },
    [letters, measure, offsets, port],
  );

  /** The letter under the finger, from where it is on the strip. */
  const pick = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const rect = strip.current?.getBoundingClientRect();
    if (rect === undefined || rect.height <= 0 || letters.length === 0) return;
    const share = (event.clientY - rect.top) / rect.height;
    jump(Math.min(letters.length - 1, Math.max(0, Math.floor(share * letters.length))));
  };

  const renderRow = useCallback(
    (row: IndexRow): JSX.Element =>
      row.kind === 'letter' ? (
        <li className="letterRow">
          <h2 className="letterHeading">{row.letter}</h2>
        </li>
      ) : (
        <li className="row artistRow">
          <button
            type="button"
            className="rowButton artistButton"
            onClick={() => {
              onOpen({ kind: 'artist', key: row.artist.key, name: row.artist.name });
            }}
          >
            <span className="artistName">{row.artist.name}</span>
            <span className="entryCount">{trackCount(row.artist.trackCount)}</span>
          </button>
        </li>
      ),
    [onOpen],
  );

  return (
    <div ref={wrap} className="artistIndex">
      <div className="azSticky" style={{ top: `${String(box.top)}px` }}>
        <div
          ref={strip}
          className="azStrip"
          role="group"
          aria-label="Буквы"
          style={{ height: `${String(box.height)}px` }}
          onPointerDown={(event) => {
            dragging.current = event.pointerId;
            try {
              event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              // A webview without pointer capture: the strip still follows while the finger is on it.
            }
            pick(event);
          }}
          onPointerMove={(event) => {
            if (dragging.current === event.pointerId) pick(event);
          }}
          onPointerUp={() => {
            dragging.current = undefined;
          }}
          onPointerCancel={() => {
            dragging.current = undefined;
          }}
        >
          {letters.map((entry, at) => (
            <button
              key={entry.letter}
              type="button"
              className="azLetter"
              aria-label={letterLabel(entry.letter)}
              aria-current={entry.letter === current ? 'true' : undefined}
              onClick={() => {
                jump(at);
              }}
            >
              {entry.letter}
            </button>
          ))}
        </div>
      </div>
      <VirtualList rows={rows} renderRow={renderRow} as="ul" ariaLabel={label} />
    </div>
  );
}
